import { Fragment } from 'react';
import {
  DataGridProProps,
  GridColDef,
  GridColDefExtended,
  GridColumnVisibilityModel,
  GridComparatorFn,
  GridValidRowModel,
  gridColumnLookupSelector,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import classNames from 'classnames/bind';
import { emptyLastSign } from './types';

import styles from './RenderCell.module.css';
const cx = classNames.bind(styles);

/** Value types a sub-column can sort on */
type SubColumnValue = string | number | null | undefined;

/** Describes a sortable sub-value of a multi-datapoint column */
export interface SubColumnDef<T extends GridValidRowModel> {
  /** Combined with the parent field to form the companion column's field (e.g. 'Container' + 'size' -> 'Container.size') */
  key: string;
  /** Text displayed for this sub-value in the column header's secondary row */
  label: string;
  valueGetter: (row: T) => SubColumnValue;
  /** Defaults to a nulls-last natural comparison when omitted */
  sortComparator?: GridComparatorFn;
}

/** Labels rendered in a column header's secondary row. Entries without a field render as plain text. */
export interface HeaderSubLabel {
  label: string;
  /** Companion sort column field; when registered on the current grid the label becomes a sort target */
  field?: string;
}

export const subColumnField = (parentField: string, key: string): string => `${parentField}.${key}`;

const defaultSubColumnComparator: GridComparatorFn<SubColumnValue> = (v1, v2, cellParams1) => {
  /** Empties go last regardless of sort direction */
  if (v1 === undefined || v1 === null || v1 === '') {
    return v2 === undefined || v2 === null || v2 === '' ? 0 : emptyLastSign(cellParams1);
  }
  if (v2 === undefined || v2 === null || v2 === '') {
    return -emptyLastSign(cellParams1);
  }

  if (typeof v1 === 'number' && typeof v2 === 'number') {
    return v1 - v2;
  }

  const s1 = typeof v1 === 'string' ? v1 : String(v1);
  const s2 = typeof v2 === 'string' ? v2 : String(v2);
  if (s1 > s2) {
    return 1;
  } else if (s1 < s2) {
    return -1;
  }
  return 0;
};

/**
 * Expands column definitions carrying `subColumns` into hidden, sort-only companion columns so a
 * sub-value sort is an ordinary GridSortModel entry (e.g. { field: 'Container.size', sort: 'asc' }).
 * Companion columns render no cells and are excluded from filtering, export, and quick filtering.
 * `hiddenFields` must be merged into the grid's columnVisibilityModel to keep them unrendered.
 */
export function expandSubColumns<T extends GridValidRowModel>(
  columns: GridColDef<T>[]
): { columns: GridColDef<T>[]; hiddenFields: GridColumnVisibilityModel } {
  const hiddenFields: GridColumnVisibilityModel = {};
  const expanded: GridColDef<T>[] = [];

  columns.forEach((column) => {
    expanded.push(column);

    (column as GridColDefExtended<T>).subColumns?.forEach((sub) => {
      const field = subColumnField(column.field, sub.key);
      hiddenFields[field] = false;

      const companion: GridColDefExtended<T> = {
        field,
        headerName: `${column.field} ${sub.label}`,
        subColumnOf: column.field,
        sortable: true,
        filterable: false,
        disableExport: true,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => sub.valueGetter(row),
        sortComparator: sub.sortComparator ?? defaultSubColumnComparator
      };
      expanded.push(companion);
    });
  });

  return { columns: expanded, hiddenFields };
}

/** Grid slotProps keeping hidden companion sort columns out of the Manage Columns panel */
export const columnsManagementSlotProps: DataGridProProps['slotProps'] = {
  columnsManagement: {
    getTogglableColumns: (columns: GridColDef[]) =>
      columns.filter((column) => !(column as GridColDefExtended<GridValidRowModel>).subColumnOf).map((column) => column.field)
  }
};

interface SubColumnSortLabelsProps {
  labels: HeaderSubLabel[];
}

/**
 * Renders a column header's secondary row of sub-value labels. A label becomes a sort target
 * (click cycles asc -> desc -> unsorted) when its companion column is registered on the current
 * grid; otherwise it renders as plain text. Must be rendered inside a DataGrid context.
 */
export function SubColumnSortLabels({ labels }: SubColumnSortLabelsProps) {
  const apiRef = useGridApiContext();
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  /** Companion-column presence is fixed for a grid instance, so read the lookup once rather than subscribing */
  const columnLookup = gridColumnLookupSelector(apiRef);

  const cycleSort = (field: string) => {
    const currentSort = sortModel.find((item) => item.field === field)?.sort;
    if (currentSort === 'desc') {
      apiRef.current.setSortModel([]);
    } else {
      apiRef.current.setSortModel([{ field, sort: currentSort === 'asc' ? 'desc' : 'asc' }]);
    }
  };

  return (
    <span className={styles['secondary-text']}>
      {labels.map((item, index) => {
        const field = item.field;
        const sortable = field !== undefined && field in columnLookup;

        if (!sortable) {
          return (
            <Fragment key={item.label}>
              {index > 0 && ', '}
              {item.label}
            </Fragment>
          );
        }

        const sortDirection = sortModel.find((sortItem) => sortItem.field === field)?.sort;

        return (
          <Fragment key={item.label}>
            {index > 0 && ', '}
            <span
              role='button'
              tabIndex={0}
              title={`Sort by ${item.label}`}
              className={cx({ 'header-sub-label': true, active: !!sortDirection })}
              onClick={(event) => {
                /** Keep the click from also triggering the header's primary column sort */
                event.stopPropagation();
                cycleSort(field);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  cycleSort(field);
                }
              }}>
              {item.label}
              {sortDirection === 'asc' && <ArrowUpwardIcon fontSize='inherit' />}
              {sortDirection === 'desc' && <ArrowDownwardIcon fontSize='inherit' />}
            </span>
          </Fragment>
        );
      })}
    </span>
  );
}
